import { InfoPopoverProps } from 'component-library';
import { cn } from 'component-library/utils';
import React, { HTMLAttributes } from 'react';
import { useTranslation } from 'react-i18next';

import { FactLike } from '@/api/rest/resources/conservation';
import {
  Capsule,
  CapsuleContent,
  CapsuleContentProps,
  CapsuleIcon,
  CapsuleIconProps,
  CapsuleLabel,
  CapsuleTrend,
} from '@/components';
import { getDisplayNumber } from '@/hooks/useDisplayNumber';
import { useFactDisplay } from '@/pages/shared/projects/pages/project/pages/conservation/shared/hooks/useFactDisplay';
import { isNullish } from '@/utils/booleans';

export type FactCapsuleProps = {
  fact: FactLike;
  label: React.ReactNode;
  signed?: ValueFromFactProps['signed'];
  trend?: ValueFromFactProps['trend'];
  nonSignificantDifferences?: ValueFromFactProps['nonSignificantDifferences'];
  infoPopoverProps?: InfoPopoverProps | null;
  capsuleIconProps?: CapsuleIconProps;
};

export const FactCapsule = ({
  fact,
  label,
  trend,
  signed,
  nonSignificantDifferences,
  infoPopoverProps,
  capsuleIconProps,
}: FactCapsuleProps) => {
  return (
    <Capsule
      label={<CapsuleLabel infoPopoverProps={infoPopoverProps}>{label}</CapsuleLabel>}
      content={
        <ValueFromFact
          fact={fact}
          trend={trend}
          signed={signed}
          nonSignificantDifferences={nonSignificantDifferences}
        />
      }
      thumbnail={<CapsuleIcon {...capsuleIconProps} />}
    />
  );
};

type ValueFromFactProps = HTMLAttributes<HTMLDivElement> & {
  fact?: FactLike;
  trend?: CapsuleContentProps['trend'];
  signed?: boolean;
  nonSignificantDifferences?: boolean;
};

export const ValueFromFact: React.FC<ValueFromFactProps> = ({
  fact,
  trend,
  className,
  signed,
  nonSignificantDifferences,
}) => {
  const { value, display } = useFactDisplay(fact, { signed });
  const { t } = useTranslation();

  if (isNullish(value)) {
    return <CapsuleContent className={cn('text-text-disabled', className)}>-</CapsuleContent>;
  }

  return (
    <CapsuleContent className={className} trend={trend}>
      {display}
      {nonSignificantDifferences && (
        <span className='text-text-disabled typography-body2 ml-1'>({t('global.analysis.nonSignificant')})</span>
      )}
    </CapsuleContent>
  );
};

export const FactCapsuleTrend = ({ trend: trendInput }: { trend?: FactLike | number }) => {
  if (isNullish(trendInput)) {
    return null;
  }

  const trend =
    typeof trendInput !== 'number' && 'value' in (trendInput as FactLike) ? (trendInput as FactLike).value : trendInput;

  if (isNullish(trend)) {
    return null;
  }

  const trendDisplay = `${getDisplayNumber(trend, undefined, {
    signDisplay: 'always',
  })}%`;

  const variant = trend < 0 ? 'negative' : 'positive';

  return <CapsuleTrend variant={variant}>{trendDisplay}</CapsuleTrend>;
};
